import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Row, Col, Badge, Card, Alert } from 'react-bootstrap';
import axios from 'axios';

const ViewPermissionRequest = ({
    show,
    onHide,
    request,
    employeeName,
    employeeCode,
    salarySettings = {},
    canApprove,
    onStatusChange
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actionLogs, setActionLogs] = useState([]);
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchActionLogs = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/permission/get_permission_logs.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: { permission_id: request.permission_id }
            });

            if (response.data.success) {
                setActionLogs(response.data.logs || []);
            }
        } catch (error) {
            console.error('Error fetching action logs:', error);
        }
    }, [API_BASE, request]);

    // Fetch action logs when modal opens
    useEffect(() => {
        if (show && request?.permission_id) {
            fetchActionLogs();
        }
    }, [show, request, fetchActionLogs]);

    const handleStatusChange = async (status, rejectionReason = '') => {
        if (!status) return;

        const confirmMessage = status === 'Approved'
            ? 'Are you sure you want to approve this permission request?'
            : status === 'Rejected'
                ? 'Are you sure you want to reject this permission request?'
                : 'Are you sure you want to cancel this permission request?';

        if (!window.confirm(confirmMessage)) return;

        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('authToken');
            const payload = {
                permission_id: request.permission_id,
                status: status
            };

            if (status === 'Rejected' && rejectionReason) {
                payload.rejection_reason = rejectionReason;
            }

            const response = await axios.post(`${API_BASE}/permission/update_permission_status.php`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                alert(`Permission request ${status.toLowerCase()} successfully`);
                onStatusChange();
                onHide();
            } else {
                setError(response.data.error || `Failed to ${status.toLowerCase()} permission request`);
            }
        } catch (error) {
            setError(error.response?.data?.error || `Failed to ${status.toLowerCase()} permission request`);
            console.error(`Error ${status.toLowerCase()}ing permission request:`, error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'Pending': { variant: 'warning', icon: 'bi-hourglass-split' },
            'Approved': { variant: 'success', icon: 'bi-check-circle' },
            'Rejected': { variant: 'danger', icon: 'bi-x-circle' },
            'Cancelled': { variant: 'secondary', icon: 'bi-slash-circle' }
        };

        const config = statusConfig[status] || { variant: 'secondary', icon: 'bi-question-circle' };

        return (
            <Badge bg={config.variant} className="d-inline-flex align-items-center">
                <i className={`bi ${config.icon} me-1`}></i>
                {status}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const time = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        return `${day}/${month}/${year} ${time}`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(hours, minutes);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const calculateDuration = (fromTime, toTime) => {
        if (!fromTime || !toTime) return '0 hours';

        const from = new Date(`1970-01-01T${fromTime}`);
        const to = new Date(`1970-01-01T${toTime}`);
        const diffMs = to - from;
        const hours = (diffMs / (1000 * 60 * 60)).toFixed(2);

        return `${hours} hours`;
    };

    const getDayName = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'long' });
    };

    const isOverLimit = () => {
        if (!salarySettings?.permission_hour || !request?.total_hours) return false;
        return parseFloat(request.total_hours) > parseFloat(salarySettings.permission_hour);
    };

    const isWeeklyOff = () => {
        if (!request?.permission_date || !salarySettings) return false;
        const dayName = getDayName(request.permission_date);
        return dayName === salarySettings.weeklyoff_1 || dayName === salarySettings.weeklyoff_2;
    };

    if (!request) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-info text-white">
                <Modal.Title>
                    <i className="bi bi-clock-history me-2"></i>
                    Permission Request Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <div>{error}</div>
                    </Alert>
                )}

                {/* Employee Information */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <div className="d-flex align-items-center">
                                    <div className="employee-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                        style={{ width: '50px', height: '50px' }}
                                    >
                                        <i className="bi bi-person fs-4"></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-1 fw-bold">{employeeName}</h5>
                                        <p className="text-muted mb-0">
                                            <i className="bi bi-person-badge me-1"></i>
                                            {employeeCode} | ID: {request.emp_id}
                                        </p>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="text-end">
                                <div className="d-flex flex-column align-items-end">
                                    <div className="mb-2">
                                        {getStatusBadge(request.status)}
                                    </div>
                                    <small className="text-muted">
                                        Request ID: <strong>PR-{request.permission_id.toString().padStart(6, '0')}</strong>
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Permission Details */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="bg-light">
                        <h6 className="mb-0 fw-bold">
                            <i className="bi bi-calendar3 me-2"></i>
                            Permission Details
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <div className="mb-3">
                                    <label className="form-label text-muted mb-1">
                                        <i className="bi bi-calendar-date me-1"></i>
                                        Date
                                    </label>
                                    <div className="d-flex align-items-center">
                                        <h6 className="mb-0 fw-bold">
                                            {formatDate(request.permission_date)}
                                        </h6>
                                        <Badge bg="secondary" className="ms-2">
                                            {getDayName(request.permission_date)}
                                        </Badge>
                                        {isWeeklyOff() && (
                                            <Badge bg="danger" className="ms-2">
                                                <i className="bi bi-exclamation-triangle me-1"></i>
                                                Weekly Off
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="mb-3">
                                    <label className="form-label text-muted mb-1">
                                        <i className="bi bi-clock me-1"></i>
                                        Time Period
                                    </label>
                                    <div>
                                        <h6 className="mb-1 fw-bold">
                                            {formatTime(request.from_time)} - {formatTime(request.to_time)}
                                        </h6>
                                        <small className="text-muted">
                                            ({calculateDuration(request.from_time, request.to_time)})
                                        </small>
                                    </div>
                                </div>
                            </Col>

                            <Col md={12}>
                                <div className="mb-3">
                                    <label className="form-label text-muted mb-1">
                                        <i className="bi bi-chat-left-text me-1"></i>
                                        Reason
                                    </label>
                                    <Card className="bg-light">
                                        <Card.Body className="py-3">
                                            <p className="mb-0">{request.reason || 'No reason provided'}</p>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Policy Check */}
                {salarySettings && (
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0 fw-bold">
                                <i className="bi bi-shield-check me-2"></i>
                                Policy Compliance Check
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                {salarySettings.permission_hour > 0 && (
                                    <Col md={6}>
                                        <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                                            <span>
                                                <i className="bi bi-clock-history me-2"></i>
                                                Hour Limit
                                            </span>
                                            <Badge bg={isOverLimit() ? 'danger' : 'success'}>
                                                {request.total_hours || calculateDuration(request.from_time, request.to_time).split(' ')[0]} / {salarySettings.permission_hour} hours
                                            </Badge>
                                        </div>
                                    </Col>
                                )}

                                {salarySettings.permission_per_month > 0 && (
                                    <Col md={6}>
                                        <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                                            <span>
                                                <i className="bi bi-calendar-check me-2"></i>
                                                Monthly Limit
                                            </span>
                                            <Badge bg="info">
                                                {salarySettings.permission_per_month} permissions/month
                                            </Badge>
                                        </div>
                                    </Col>
                                )}

                                {(salarySettings.weeklyoff_1 || salarySettings.weeklyoff_2) && (
                                    <Col md={12}>
                                        <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                                            <span>
                                                <i className="bi bi-calendar-event me-2"></i>
                                                Weekly Off Days
                                            </span>
                                            <div>
                                                {salarySettings.weeklyoff_1 && (
                                                    <Badge bg="secondary" className="me-1">
                                                        {salarySettings.weeklyoff_1}
                                                    </Badge>
                                                )}
                                                {salarySettings.weeklyoff_2 && (
                                                    <Badge bg="secondary">
                                                        {salarySettings.weeklyoff_2}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                {/* Action History */}
                {actionLogs.length > 0 && (
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0 fw-bold">
                                <i className="bi bi-list-check me-2"></i>
                                Action History
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="timeline">
                                {actionLogs.map((log, index) => (
                                    <div key={log.log_id} className="timeline-item">
                                        <div className="timeline-marker"></div>
                                        <div className="timeline-content">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <h6 className="mb-0">
                                                    <Badge bg={
                                                        log.action === 'Approved' ? 'success' :
                                                            log.action === 'Rejected' ? 'danger' :
                                                                log.action === 'Cancelled' ? 'secondary' :
                                                                    'info'
                                                    }>
                                                        {log.action}
                                                    </Badge>
                                                </h6>
                                                <small className="text-muted">
                                                    {formatDateTime(log.created_at)}
                                                </small>
                                            </div>
                                            <small className="text-muted">
                                                Performed by User ID: {log.performed_by}
                                            </small>
                                            {log.details && (
                                                <div className="mt-2 p-2 bg-light rounded">
                                                    <small className="text-muted">{log.details}</small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Close
                </Button>

                {request.status === 'Pending' && (
                    <>
                        {canApprove && (
                            <>
                                <Button
                                    variant="success"
                                    onClick={() => handleStatusChange('Approved')}
                                    disabled={loading || isOverLimit() || isWeeklyOff()}
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    ) : (
                                        <i className="bi bi-check-lg me-2"></i>
                                    )}
                                    Approve
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => {
                                        const reason = window.prompt('Enter rejection reason (optional):');
                                        handleStatusChange('Rejected', reason);
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    ) : (
                                        <i className="bi bi-x-lg me-2"></i>
                                    )}
                                    Reject
                                </Button>
                            </>
                        )}
                        <Button
                            variant="warning"
                            onClick={() => {
                                const confirmed = window.confirm('Are you sure you want to cancel this permission request?');
                                if (confirmed) handleStatusChange('Cancelled');
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            ) : (
                                <i className="bi bi-slash-circle me-2"></i>
                            )}
                            Cancel Request
                        </Button>
                    </>
                )}

                {(isOverLimit() || isWeeklyOff()) && request.status === 'Pending' && (
                    <Alert variant="warning" className="w-100 mb-0">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {isOverLimit() ? 'Permission exceeds hour limit' : 'Permission requested on weekly off day'}
                    </Alert>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// Add some CSS for the timeline
const timelineStyles = `
  .timeline {
    position: relative;
    padding-left: 20px;
  }
  
  .timeline::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #dee2e6;
  }
  
  .timeline-item {
    position: relative;
    margin-bottom: 20px;
  }
  
  .timeline-marker {
    position: absolute;
    left: -20px;
    top: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #6c757d;
    border: 2px solid white;
  }
  
  .timeline-content {
    margin-left: 10px;
  }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = timelineStyles;
    document.head.appendChild(style);
}

export default ViewPermissionRequest;